import PhoneInput, { isValidPhoneNumber, parsePhoneNumber } from 'react-phone-number-input';
import 'react-phone-number-input/style.css';

export { isValidPhoneNumber };

// Maximum national (significant) number length per country. The library's
// `limitMaxLength` only caps to the loosest length across ALL of a country's
// number types (13 digits for India), so on its own it still lets a user type
// an 11-13 digit Indian number. We hard-cap the exact mobile length here.
// Countries not listed fall back to the library's default `limitMaxLength`.
const MAX_NSN_LENGTH = { IN: 10 };

// Parses `value` (E.164) and returns the per-country cap only when one applies.
function nationalCap(value) {
  if (!value) return null;
  try {
    const parsed = parsePhoneNumber(value);
    if (!parsed || !parsed.country) return null;
    const max = MAX_NSN_LENGTH[parsed.country];
    return max ? { parsed, max } : null;
  } catch (_) {
    return null; // partial / unparseable input while typing
  }
}

// Reusable international phone input. Defaults to India (+91), lets the user pick
// any country, and emits a clean E.164 string (e.g. "+919876543210") or '' when empty.
export default function PhoneField({ value, onChange, placeholder = 'Enter phone number', className = '', compact = false, ...rest }) {
  // Block a keystroke that would push the national number past the country's max
  // (e.g. the 11th digit for India). input-format honours preventDefault() here.
  const handleKeyDown = (e) => {
    if (e.key < '0' || e.key > '9' || e.ctrlKey || e.metaKey || e.altKey) return;
    const input = e.target;
    if (input.selectionStart !== input.selectionEnd) return; // replacing a selection — net length won't grow
    const cap = nationalCap(value);
    if (cap && cap.parsed.nationalNumber.length >= cap.max) e.preventDefault();
  };

  // Truncate pasted / programmatic values that arrive over the cap in one shot.
  const handleChange = (v) => {
    const cap = nationalCap(v);
    if (cap && cap.parsed.nationalNumber.length > cap.max) {
      onChange(`+${cap.parsed.countryCallingCode}${cap.parsed.nationalNumber.slice(0, cap.max)}`);
      return;
    }
    onChange(v || '');
  };

  // `compact` matches the sibling email/name textbox size (px-3 py-2.5 text-sm) and
  // background (bg-white, the public apply form's fixed light look) instead of the
  // library's roomier/theme-following default — kept opt-in via className's baked-in
  // classes so it can't be overridden by cascade order (px-4/bg-background always
  // beat an appended px-3/bg-white in Tailwind's generated stylesheet, regardless of
  // source order).
  const sizeCls = compact ? 'px-3 py-2.5 text-base md:text-sm bg-white' : 'px-4 py-3 bg-background';
  return (
    <PhoneInput
      international
      limitMaxLength
      defaultCountry="IN"
      value={value || undefined}
      onChange={handleChange}
      placeholder={placeholder}
      className={`fb-phone w-full border border-border rounded-lg ${sizeCls} focus-within:border-[#10b981] ${className}`}
      numberInputProps={{ className: `bg-transparent border-0 outline-none text-text-primary font-mono w-full${compact ? ' text-base md:text-sm' : ''}`, onKeyDown: handleKeyDown }}
      {...rest}
    />
  );
}
